import React from "react";
import { aiAddMessage } from "../api/api";
import { endRun as endMetricsRun, ensureRun, mark, setRunMetricsEnabled, timer } from "../state/ai/runMetrics";
import { PANE_ID } from "../state/ai/pane";
import { beginRun, endRun } from "../state/ai/runRegistry";
import { useChatHistory } from "../state/api/ai/useChatHistory";
import { useSkills } from "../state/api/ai/useSkills";
import { useUsage } from "../state/api/ai/useUsage";
import { useSession } from "../state/api/session/useSession";
import { AI_TOOL, ChatHistory, ChatMessage, INTERACTION_TYPE } from "../types";
import { findContainerFileId, findContainerFileIdInHistory } from "../util/containerFiles";
import { getWorkbookContainerBlock } from "../util/officeFile";
import useAiTools from "./useAiTools";
import useApi from "./useApi";

export function useAiChat(history: ChatHistory) {
  const { skill } = history;

  const mounted = React.useRef(true);

  const [thinkingSince, setThinkingSince] = React.useState<number | null>(null);

  const { mutate, chatHistory, isLoading } = useChatHistory({ skill }, { enabled: !!skill });
  const { reloadUsage } = useUsage();
  const { skillsById } = useSkills();
  const { session } = useSession();

  React.useEffect(() => {
    setRunMetricsEnabled(!!session?.isAdmin);
  }, [session?.isAdmin]);

  // Only the analysis panel is a one-shot run. An interactive chat must always get
  // its closing turn, even on the rare path where it delivers through the tool.
  const isAnalysis = skillsById?.[skill]?.type === INTERACTION_TYPE.ANALYSIS;
  const { tools } = useAiTools();

  React.useEffect(() => () => { mounted.current = false; }, []);

  const callApi = useApi();

  const addMessage = async (initialContent?: Record<string, any>[], externalSignal?: AbortSignal) => {

    setThinkingSince(Date.now());

    const ownsMetricsRun = ensureRun(skill);

    mark(skill, "addMessage:start", { hasInitialContent: !!initialContent });

    const controller = beginRun(skill);

    externalSignal?.addEventListener("abort", () => controller.abort(), { once: true });

    controller.signal.onabort = () => {
      console.warn("Processing aborted");
    };

    const { signal } = controller;

    // The model writes its file with a code execution command in one turn and calls
    // the tool that delivers it in a later one, so the id has to survive across turns
    // instead of being looked up in the current message only.
    let containerFileId = findContainerFileIdInHistory((chatHistory as any[]) ?? []);
    const consumed = new Set<string>();

    if (initialContent) {
      await mutate((prev: any) => [
        ...(prev ?? []),
        {
          content: initialContent,
          contact: "LOCAL",
          history: "LOCAL",
          role: "user",
          timestamp: new Date()
        }
      ], { revalidate: false });
    }

    const executeTurn = async (currentBlocks?: Record<string, any>[]): Promise<ChatMessage | null> => {

      if (signal.aborted) return null;

      const tempId = `temp_assistant_${Date.now()}`;
      const partialJsonStrings: Record<number, string> = {};

      const turnDone = timer(skill, "api:turn");
      const firstByteDone = timer(skill, "api:firstByte");

      let sawFirstByte = false;
      let eventCount = 0;

      mark(skill, "api:request", { blocks: currentBlocks?.length ?? 0 });

      await mutate((prev: any) => ([
        ...(prev ?? []),
        {
          _id: tempId,
          content: [],
          role: "assistant",
          contact: "LOCAL",
          history: "LOCAL",
          timestamp: new Date()
        }
      ]), { revalidate: false });


      const result = await callApi(
        aiAddMessage(
          {
            skill,
            content: currentBlocks,
            pane: PANE_ID
          },
          {
            abortSignal: signal
          },
          async (event: Record<string, any>) => {

            eventCount++;

            if (!sawFirstByte) {
              sawFirstByte = true;
              firstByteDone();
            }

            await mutate((prev: any) => {
              if (!prev) return prev;

              return prev.map((m: any) => {
                if (m._id !== tempId) return m;

                // Clone the message and content array to force React re-render
                const updatedMsg = { ...m, content: [...(m.content || [])] };
                const i = event.index;
                const currentBlock = updatedMsg.content[i] || {};

                try {
                  switch (event.type) {
                    case "block_start":
                      updatedMsg.content[i] = { type: "text", ...event.block };

                      if (updatedMsg.content[i].type === "tool_use") {
                        updatedMsg.content[i].input = {};
                        partialJsonStrings[i] = "";
                      }
                      break;

                    case "block_delta":

                      if (!updatedMsg.content[i]) {
                        break;
                      }

                      updatedMsg.content[i] = { ...currentBlock };

                      if (event.text) {
                        updatedMsg.content[i].text = (updatedMsg.content[i].text || "") + event.text;
                      }
                      else if (event.partial_json) {
                        partialJsonStrings[i] += event.partial_json;
                        try {
                          updatedMsg.content[i].input = JSON.parse(partialJsonStrings[i]);
                        } catch {
                          // Silent fail until JSON string is complete
                        }
                      }
                      break;

                    case "block_stop":
                      updatedMsg.content[i] = { ...currentBlock, isComplete: true };

                      if (currentBlock.type === "tool_use" && partialJsonStrings[i]) {
                        try {
                          updatedMsg.content[i].input = JSON.parse(partialJsonStrings[i]);
                        } catch { }
                      }
                      break;
                  }
                } catch (err) {
                  console.error("Mutation error", err);
                }

                return updatedMsg;
              });
            }, { revalidate: false });
          }
        )
      );

      if (signal.aborted) return null;

      switch (result.success) {
        case false:
          const errorMessage: ChatMessage = {
            content: [{
              type: "text",
              text: result.message
            }],
            role: "assistant",
            contact: "LOCAL",
            history: "LOCAL",
            timestamp: new Date()
          };

          await mutate((prev: any) => (prev ?? []).map((m: any) => m._id === tempId ? errorMessage : m), { revalidate: false });
          return null;
      }

      const agentMsg = result.data;

      turnDone({
        events: eventCount,
        blocks: Array.isArray(agentMsg?.content) ? agentMsg.content.length : 0,
        inputTokens: agentMsg?.inputTokens ?? null,
        outputTokens: agentMsg?.outputTokens ?? null,
        cacheReadTokens: agentMsg?.inputTokensCacheRead ?? null,
        cacheWriteTokens: agentMsg?.inputTokensCacheWrite ?? null,
        responseId: agentMsg?.responseId ?? null
      });

      containerFileId = findContainerFileId(agentMsg.content) ?? containerFileId;

      await mutate((prev: any) => (prev ?? []).map((m: any) => m._id === tempId ? agentMsg : m), { revalidate: false });

      const toolBlocks = Array.isArray(agentMsg.content)
        ? agentMsg.content.filter((block: any) => block.type === "tool_use")
        : null;

      if (toolBlocks?.length) {
        const c: any[] = [];

        // Container uploads ride along in the same user turn as the tool
        // results, appended after them, which is where the API expects them.
        const uploads: Record<string, any>[] = [];

        // In the analysis panel submit_analysis_result hands the finished result
        // straight to the UI, so once it succeeds there is nothing left for the model
        // to say and sending the result back buys a round trip for a line nobody reads.
        let delivered = false;

        for (const b of toolBlocks) {
          if (signal.aborted) return null;
          let executionResult = "";

          const tool = (tools as any)[b.name];

          const toolDone = timer(skill, `tool:${b.name}`);

          if (b.name === AI_TOOL.GET_WORKBOOK_FILE) {
            try {
              const block = await getWorkbookContainerBlock(skill);
              uploads.push(block);
              executionResult = `Success: "${block.title}" is in your container under /files/input/. Locate it with a bash_code_execution command: find /files/input -type f -name '*.xlsx'`;
            }
            catch (err: unknown) {
              executionResult = `Error: ${err instanceof Error ? err.message : "the workbook could not be read"}`;
            }
          }
          else if (!tool) {
            executionResult = `Error: Tool '${b.name}' is not defined`;
          }
          else if (b.name === AI_TOOL.APPLY_WORKBOOK_PATCH || b.name === AI_TOOL.SUBMIT_ANALYSIS_RESULT) {
            // Only a file this run has not already handed over counts. Walking the
            // history fixes the split-turn case, but apply_workbook_patch writes to
            // the user's sheet, so it must never silently re-apply an old plan.
            const id = containerFileId && !consumed.has(containerFileId) ? containerFileId : null;

            executionResult = id
              ? await tool({ ...b.input, file_id: id })
              : "Error: no new file was found in your code execution results. Write the file with a code execution command first, then call this tool once that command has returned.";

            if (id && !executionResult.startsWith("Error")) {
              consumed.add(id);
            }
          }
          else {
            executionResult = await tool(b.input);
          }

          if (b.name === AI_TOOL.SUBMIT_ANALYSIS_RESULT && executionResult.startsWith("Success")) {
            delivered = true;
          }

          toolDone({ ok: !executionResult.startsWith("Error"), resultLength: executionResult.length });

          const toolResult = {
            type: "tool_result",
            tool_use_id: b.id,
            content: executionResult
          };

          c.push(toolResult);
        }

        c.push(...uploads);

        await mutate((prev: any) => [
          ...(prev ?? []),
          {
            content: c,
            contact: "LOCAL",
            history: "LOCAL",
            role: "user",
            timestamp: new Date()
          }
        ], { revalidate: false });

        if (signal.aborted) return null;

        // Only a lone successful delivery ends it. A failure still goes back so the
        // model can re-run and resubmit, and anything alongside it still needs its
        // result. The backend drops the unanswered tool_use from later history.
        if (isAnalysis && delivered && toolBlocks.length === 1 && !uploads.length) {
          return agentMsg;
        }

        return await executeTurn(c);
      }

      return agentMsg;
    };

    try {
      await executeTurn(initialContent);
    }
    finally {
      endRun(skill, controller);

      mark(skill, "addMessage:end", { aborted: signal.aborted });

      if (ownsMetricsRun) endMetricsRun(skill);

      if (mounted.current) {
        setThinkingSince(null);
      }

      if (!signal.aborted) {
        // Not part of the run: awaiting it held the progress dialog open for
        // another round trip after the result was already on screen.
        void reloadUsage();
      }
    }
  };

  return {
    thinkingSince,
    chatHistory,
    isLoading,
    mutate,
    addMessage
  };
}